// Receiving rules shared by the server (receipt.post) and the receiving screen, so both agree on what
// a count means: what's still expected, what's over, when a PO is complete, and which line a scan hits.
import { hasSkuPrefix, skuStem } from "./part-sku.ts";
import { mulRound, type PoStatus } from "./po.ts";

/** POs a delivery can be counted against: sent (goods can arrive early) until closed. */
export const RECEIVABLE_STATUSES: readonly PoStatus[] = ["sent", "acknowledged", "in_production", "shipped", "received"];
export const canReceive = (status: PoStatus) => RECEIVABLE_STATUSES.includes(status);

export const REJECT_REASONS = ["damaged", "defective", "wrong_item", "other"] as const;
export type RejectReason = (typeof REJECT_REASONS)[number];
export const REJECT_REASON_LABELS: Record<RejectReason, string> = {
  damaged: "Damaged",
  defective: "Defective",
  wrong_item: "Wrong item",
  other: "Other",
};

export const STOCK_REASONS = ["receipt", "adjust", "count", "consign_ship", "consume", "make_output", "scrap", "return", "presale_ship"] as const;
export type StockReason = (typeof STOCK_REASONS)[number];
export const STOCK_REASON_LABELS: Record<StockReason, string> = {
  receipt: "Received",
  adjust: "Adjustment",
  count: "Count",
  consign_ship: "Shipped to CM",
  consume: "Used in a build",
  make_output: "Built",
  scrap: "Scrapped",
  return: "Returned",
  presale_ship: "Sent to backers",
};

const q = (n: number) => mulRound(n, 1, 3);
/**
 * Pre-sales --- qty_direct: units of a make PO line that go straight from the CM to backers, never to HHL;
 * qty_direct_shipped: of them, sent; qty_output: what its make order reports built (make PO lines only).
 */
type Counted = {
  qty: number;
  qty_received: number;
  qty_rejected?: number | null;
  qty_direct?: number | null;
  qty_direct_shipped?: number | null;
  qty_output?: number | null;
  /** Its PO's po.hhl_share_closed: "nothing else is coming" to HHL, while the backers' units are still to do. */
  hhl_closed?: boolean | null;
};

/**
 * Pre-sales --- a line's units for backers that are done: built (the CM's output beyond what was delivered here) or
 * sent to backers. Never more than its qty_direct. Units count as built only when the CM reports them, never because
 * the units for HHL have arrived.
 */
export const backersDone = (line: Counted) => {
  const direct = Number(line.qty_direct ?? 0);
  if (direct <= 0) return 0;
  const built = Number(line.qty_output ?? 0) - Number(line.qty_received) - Number(line.qty_rejected ?? 0);
  return Math.max(0, Math.min(direct, q(Math.max(Number(line.qty_direct_shipped ?? 0), built))));
};

/**
 * Pre-sales --- for billing: a line's units delivered to backers: shipped to them (all of them: after a release, units
 * shipped beyond qty_direct were still built and delivered), or built for them (the CM's output beyond what HHL
 * received and rejected, up to qty_direct: HHL's own units still at the CM aren't delivered), whichever is more, never
 * more than the line has left after HHL's (qty − received). Lines with nothing for backers deliver only what HHL received.
 */
export const backersDelivered = (line: Counted) => {
  const direct = Number(line.qty_direct ?? 0);
  const shipped = Number(line.qty_direct_shipped ?? 0);
  if (direct <= 0 && shipped <= 0) return 0;
  const built = Math.min(direct, Number(line.qty_output ?? 0) - Number(line.qty_received) - Number(line.qty_rejected ?? 0));
  const left = Number(line.qty) - Number(line.qty_received);
  return Math.max(0, Math.min(left, q(Math.max(shipped, built))));
};

/**
 * Pre-sales --- what a line expects at its ship-to: what was ordered, less the units that go straight from the CM to
 * backers (the PO keeps its quantity and its bill: the CM is paid for every unit).
 */
export const expectedHere = (line: Counted) => q(Number(line.qty) - Number(line.qty_direct ?? 0));

/** Good units still expected on a line (never below 0). Rejected units don't fill a line: the vendor owes them. */
export const outstanding = (line: Counted) => (line.hhl_closed ? 0 : Math.max(0, q(expectedHere(line) - Number(line.qty_received))));

/** Pre-sales --- HHL has all it's getting from a line: what's expected here arrived, or nothing else is coming. */
export const hhlDone = (line: Counted) => !!line.hhl_closed || Number(line.qty_received) >= expectedHere(line);

/** Pre-sales --- a line's units for backers the CM still has to build or ship. */
export const backersLeft = (line: Counted) => Math.max(0, q(Number(line.qty_direct ?? 0) - backersDone(line)));

/** How far `received` more good units take a line past what's expected (0 when they don't). */
export const overBy = (line: Counted, received: number) => Math.max(0, q(Number(line.qty_received) + received - expectedHere(line)));

/**
 * Every line has all its good units (what's expected here, or nothing else is coming) and, Pre-sales, its units for
 * backers are built or sent: until they are, a make PO isn't received and its make order stays open.
 */
export const poComplete = (lines: Counted[]) => lines.length > 0 && lines.every((l) => hhlDone(l) && backersLeft(l) <= 0);

/** A Shopify item whose stock Shopify tracks: its receipts go to Shopify, not the ledger. */
export const postsToShopify = (item: { source: string; shopify_inventory_item_id: string | null; shopify_tracked: boolean | null }) =>
  item.source === "shopify" && !!item.shopify_inventory_item_id && item.shopify_tracked !== false;

/** A local item with stock (services have none): its receipts become stock moves. */
export const keepsLedger = (item: { source: string; kind: string }) => item.source === "local" && item.kind !== "service";

export type ScanTarget = { id: string; barcode: string; sku: string; vendor_sku: string; done: boolean };

/**
 * The line a scanned code (or typed SKU) counts against: barcode first, then our SKU, then the
 * vendor's, then our SKU without its type prefix (GCU2-RM2 for MAT-GCU2-RM2: shared/part-sku.ts). With the same item on
 * two lines, the first that still needs units wins.
 */
export function matchScan<T extends ScanTarget>(code: string, lines: T[]): T | undefined {
  const want = code.trim().toLowerCase();
  if (!want) return undefined;
  const codes: ((l: T) => string)[] = [(l) => l.barcode, (l) => l.sku, (l) => l.vendor_sku, (l) => (hasSkuPrefix(l.sku) ? skuStem(l.sku) : "")];
  for (const codeOf of codes) {
    const hits = lines.filter((l) => codeOf(l)?.trim().toLowerCase() === want);
    if (hits.length) return hits.find((l) => !l.done) ?? hits[0];
  }
  return undefined;
}
